import { useMutation, UseMutationOptions, useQuery, useQueryClient, UseQueryOptions } from 'react-query';

import type {
  CreateGeoCarvingSessionRequest,
  GeoNameMappingEntry,
  GeoCarvingResults,
  GeoCarvingSession,
  RunGeoCarvingRequest,
  ScheduledTaskResponse,
} from 'components/SPM/GeoCarving/types';
import { usePlanPrefetchBootstrap } from 'contexts/PlanPrefetchBootstrapContext';
import { useTQPrefetchTokenRegistry } from 'contexts/TQPrefetchTokenContext';
import { getRequest, postRequest } from 'services/apiRequests';
import type { StringifiedUUID } from 'types';
import type { APIError } from 'utils/errors';
import { CSRFService } from 'services/csrf';

const getApiUrl = (hierarchyModelId: StringifiedUUID, objectId: StringifiedUUID) =>
  `/api/v1/tq/hierarchy-models/${hierarchyModelId}/objects/${objectId}/geo-carving/`;

const geoCarvingKeys = {
  base: (hierarchyModelId: string) => ['hierarchy-models', hierarchyModelId, 'geo-carving'] as const,
  session: (hierarchyModelId: string, objectId: string) =>
    [...geoCarvingKeys.base(hierarchyModelId), objectId, 'session'] as const,
  carveResults: (hierarchyModelId: string, objectId: string) =>
    [...geoCarvingKeys.base(hierarchyModelId), objectId, 'carve-results'] as const,
  geoNameMapping: (hierarchyModelId: string, objectId: string) =>
    [...geoCarvingKeys.base(hierarchyModelId), objectId, 'geo-name-mapping'] as const,
};

const getGeoCarvingSession = (hierarchyModelId: StringifiedUUID, objectId: StringifiedUUID) =>
  getRequest<null, GeoCarvingSession>(getApiUrl(hierarchyModelId, objectId));

const createGeoCarvingSession = (
  hierarchyModelId: StringifiedUUID,
  objectId: StringifiedUUID,
  params: CreateGeoCarvingSessionRequest,
  csrfToken: string,
) =>
  postRequest<CreateGeoCarvingSessionRequest, ScheduledTaskResponse>(
    getApiUrl(hierarchyModelId, objectId),
    params,
    csrfToken,
  );

type UseGetGeoCarvingSessionQueryOptions = Omit<UseQueryOptions<GeoCarvingSession, APIError>, 'queryKey' | 'queryFn'>;

const useGetGeoCarvingSessionQuery = (
  hierarchyModelId: StringifiedUUID | undefined,
  objectId: StringifiedUUID | undefined,
  options: UseGetGeoCarvingSessionQueryOptions = {
    enabled: true,
  },
) => {
  const queryKey = geoCarvingKeys.session(hierarchyModelId as string, objectId as string);
  const queryClient = useQueryClient();
  const query = useQuery<GeoCarvingSession, APIError>(
    queryKey,
    () => getGeoCarvingSession(hierarchyModelId as string, objectId as string),
    {
      enabled: !!hierarchyModelId && !!objectId && options.enabled,
      ...options,
    },
  );
  const invalidateQuery = () => queryClient.invalidateQueries(queryKey);
  return { query, invalidateQuery };
};

const useCreateGeoCarvingSessionMutation = (
  hierarchyModelId: StringifiedUUID | undefined,
  objectId: StringifiedUUID | undefined,
  options: UseMutationOptions<ScheduledTaskResponse, APIError, CreateGeoCarvingSessionRequest> = {},
) => {
  const { query: csrfQuery } = CSRFService.useGetCSRFToken();
  const csrfToken = csrfQuery.data?.csrf ?? '';

  const queryClient = useQueryClient();
  const queryKey = geoCarvingKeys.session(hierarchyModelId as string, objectId as string);

  return useMutation<ScheduledTaskResponse, APIError, CreateGeoCarvingSessionRequest>(
    (params: CreateGeoCarvingSessionRequest) =>
      createGeoCarvingSession(hierarchyModelId as string, objectId as string, params, csrfToken),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries(queryKey);
        options.onSuccess?.(data, variables, context);
      },
    },
  );
};

const getCarveResults = (hierarchyModelId: StringifiedUUID, objectId: StringifiedUUID) =>
  getRequest<null, GeoCarvingResults>(`${getApiUrl(hierarchyModelId, objectId)}carve-results/`);

const getGeoNameMapping = (hierarchyModelId: StringifiedUUID, objectId: StringifiedUUID) =>
  getRequest<null, GeoNameMappingEntry[]>(`${getApiUrl(hierarchyModelId, objectId)}geo-name-mapping/`);

type UseGetCarveResultsQueryOptions = Omit<UseQueryOptions<GeoCarvingResults, APIError>, 'queryKey' | 'queryFn'>;

const useGetCarveResultsQuery = (
  hierarchyModelId: StringifiedUUID | undefined,
  objectId: StringifiedUUID | undefined,
  options: UseGetCarveResultsQueryOptions = { enabled: true },
) => {
  const queryKey = geoCarvingKeys.carveResults(hierarchyModelId as string, objectId as string);
  const queryClient = useQueryClient();
  const query = useQuery<GeoCarvingResults, APIError>(
    queryKey,
    () => getCarveResults(hierarchyModelId as string, objectId as string),
    {
      enabled: !!hierarchyModelId && !!objectId && options.enabled,
      ...options,
    },
  );
  const invalidateQuery = () => queryClient.invalidateQueries(queryKey);
  return { query, invalidateQuery };
};

type UseGetGeoNameMappingQueryOptions = Omit<UseQueryOptions<GeoNameMappingEntry[], APIError>, 'queryKey' | 'queryFn'>;

const useGetGeoNameMappingQuery = (
  hierarchyModelId: StringifiedUUID | undefined,
  objectId: StringifiedUUID | undefined,
  options: UseGetGeoNameMappingQueryOptions = { enabled: true },
) => {
  const queryKey = geoCarvingKeys.geoNameMapping(hierarchyModelId as string, objectId as string);
  const queryClient = useQueryClient();
  const query = useQuery<GeoNameMappingEntry[], APIError>(
    queryKey,
    () => getGeoNameMapping(hierarchyModelId as string, objectId as string),
    {
      enabled: !!hierarchyModelId && !!objectId && options.enabled,
      ...options,
    },
  );
  const invalidateQuery = () => queryClient.invalidateQueries(queryKey);
  return { query, invalidateQuery };
};

const runGeoCarve = (
  hierarchyModelId: StringifiedUUID,
  objectId: StringifiedUUID,
  params: RunGeoCarvingRequest,
  csrfToken: string,
) =>
  postRequest<RunGeoCarvingRequest, ScheduledTaskResponse>(
    `${getApiUrl(hierarchyModelId, objectId)}carve/`,
    params,
    csrfToken,
  );

const useRunGeoCarveMutation = (
  hierarchyModelId: StringifiedUUID | undefined,
  objectId: StringifiedUUID | undefined,
  options: UseMutationOptions<ScheduledTaskResponse, APIError, RunGeoCarvingRequest> = {},
) => {
  const { query: csrfQuery } = CSRFService.useGetCSRFToken();
  const csrfToken = csrfQuery.data?.csrf ?? '';

  const queryClient = useQueryClient();
  const sessionQueryKey = geoCarvingKeys.session(hierarchyModelId as string, objectId as string);
  const carveResultsQueryKey = geoCarvingKeys.carveResults(hierarchyModelId as string, objectId as string);

  return useMutation<ScheduledTaskResponse, APIError, RunGeoCarvingRequest>(
    (params: RunGeoCarvingRequest) => runGeoCarve(hierarchyModelId as string, objectId as string, params, csrfToken),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries(sessionQueryKey);
        queryClient.invalidateQueries(carveResultsQueryKey);
        options.onSuccess?.(data, variables, context);
      },
    },
  );
};

const applyCarveResults = (
  hierarchyModelId: StringifiedUUID,
  objectId: StringifiedUUID,
  prefetchToken: string,
  csrfToken: string,
) =>
  postRequest<null, void>(
    `${getApiUrl(hierarchyModelId, objectId)}apply/?prefetch_token=${encodeURIComponent(prefetchToken)}`,
    null,
    csrfToken,
  );

const useApplyCarveResultsMutation = (
  hierarchyModelId: StringifiedUUID | undefined,
  objectId: StringifiedUUID | undefined,
  options: UseMutationOptions<void, APIError, void> = {},
) => {
  const { query: csrfQuery } = CSRFService.useGetCSRFToken();
  const csrfToken = csrfQuery.data?.csrf ?? '';
  const { prefetchKeyParams } = usePlanPrefetchBootstrap();
  const { getTokenFromParams } = useTQPrefetchTokenRegistry();

  return useMutation<void, APIError, void>(async () => {
    const prefetchToken = getTokenFromParams(prefetchKeyParams.accounts);
    if (!prefetchToken) {
      throw new Error('GeocarvingPage.missing_prefetch_token');
    }
    return applyCarveResults(hierarchyModelId as string, objectId as string, prefetchToken, csrfToken);
  }, options);
};

export const GeoCarvingService = {
  keys: geoCarvingKeys,
  getApiUrl,
  applyCarveResults,
  useGetGeoCarvingSessionQuery,
  useCreateGeoCarvingSessionMutation,
  useGetCarveResultsQuery,
  useGetGeoNameMappingQuery,
  useRunGeoCarveMutation,
  useApplyCarveResultsMutation,
};
